import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LinearProgress } from '@material-ui/core';
import { FormikErrors, useFormik } from 'formik';
import Fuse from 'fuse.js';
import React, { useEffect, useMemo, useState } from 'react';
import VoxelButton from '../../../../../components/voxel/Buttons/VoxelButton';
import { GenericOption } from '../../../../../components/voxel/Forms/VoxelAutocomplete';
import VoxelInput from '../../../../../components/voxel/Forms/VoxelInput';
import VoxelSelectItem from '../../../../../components/voxel/Forms/VoxelSelectItem';
import VoxelDivider from '../../../../../components/voxel/VoxelDivider';
import { TagGroupGq } from '../../../../../graphql/projects/models/TagGroupGq';
import { projectsApiService } from '../../../../../graphql/projects/projectsApiService';
import VoxelRadio from '../../../../../components/voxel/Forms/VoxelRadio';
import VoxelAlert from '../../../../../components/voxel/Alert/VoxelAlert';
import { useDebounceValue } from '../../../../../logic/otherUtilities/useDebounceValue';

export interface CellApplyTagPopoverProps {
  tagGroup: TagGroupGq;
  onSuccess: (tagId: number, selection?: boolean) => Promise<any>;

  tagPanelOpen?: boolean;
  openTagPanel?: (id?: number) => void;
  handleClose?: () => void;
  noKeywordsSelected?: boolean;
}

interface FormState {
  tagGroupId: number;
  tag?: GenericOption;
  selection: 'keyword' | 'selection';
}

const CellApplyTagPopover: React.FC<CellApplyTagPopoverProps> = ({
  tagGroup,
  onSuccess,
  openTagPanel,
  tagPanelOpen,
  handleClose,
  noKeywordsSelected,
}) => {
  // Apply tag
  const addTagsMutation = projectsApiService.mutations.tags.useAddTags();

  const selectionMemoryKey = 'tag-selection';
  const selectionMemory: any = localStorage.getItem(selectionMemoryKey) ?? 'keyword';
  const formikProps = useFormik<FormState>({
    initialValues: {
      tagGroupId: tagGroup.id,
      tag: null,
      selection: selectionMemory,
    },
    validate: (values) => {
      const errors: FormikErrors<FormState> = {};
      if (!values?.tag?.title?.length) {
        errors.tag = errors.tag ?? {};
        errors.tag.title = 'Cannot be null';
      }
    },
    onSubmit: (values, actions) => {
      if (tagGroup.tags.findIndex((t) => t.name === values.tag.title.trim()) >= 0) {
        return onSuccess(values.tag.value, values.selection === 'selection');
      } else {
        return addTagsMutation({
          projectId: tagGroup.projectId,
          tagGroupId: values.tagGroupId,
          tags: [
            {
              name: values.tag.title,
              textMatchVariants: values.tag.title,
            },
          ],
        }).then((tag) => onSuccess(tag[0]?.id ?? null, values.selection === 'selection'));
      }
    },
  });

  useEffect(() => {
    localStorage.setItem(selectionMemoryKey, formikProps.values.selection);
  }, [formikProps.values.selection]);

  const tagOptions: GenericOption[] = useMemo(
    () =>
      tagGroup.tags.map((tag) => ({
        value: tag.id,
        title: tag.name,
      })),
    [tagGroup?.tags]
  );
  const options: GenericOption[] = useMemo(() => {
    return tagOptions;
  }, [tagOptions]);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounceValue(searchTerm, 100);
  const filteredOptions = useMemo(
    () =>
      options.filter(
        (opt) =>
          !debouncedSearchTerm.length ||
          opt.title?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      ),
    [debouncedSearchTerm, options]
  );

  const handleFreeAdd = () => {
    let option: GenericOption = { value: searchTerm, title: searchTerm };
    const similarOption = options.find(
      (opt) => opt.title?.toLowerCase() === option.title?.toLowerCase()
    );
    option = similarOption ?? option;

    formikProps.setFieldValue('tag', option).then(formikProps.submitForm);
  };

  return (
    <div className="w-64">
      <div className="py-4 px-5 flex flex-col gap-2 bg-gray-50 border-b border-gray-200 opacity-80">
        <div className={formikProps.values.selection === 'keyword' ? 'opacity-100' : 'opacity-75'}>
          <VoxelRadio
            value={'keyword'}
            title={'Tag keyword'}
            selectedOption={formikProps.values.selection}
            setSelectedOption={(value) => formikProps.setFieldValue('selection', value)}
          />
        </div>
        <div
          className={formikProps.values.selection === 'selection' ? 'opacity-100' : 'opacity-75'}
        >
          <VoxelRadio
            value={'selection'}
            title={'Tag selection'}
            selectedOption={formikProps.values.selection}
            setSelectedOption={(value) => formikProps.setFieldValue('selection', value)}
          />
        </div>
        {noKeywordsSelected && formikProps.values.selection === 'selection' && (
          <div className="pt-2">
            <VoxelAlert size="xs" iconSize="sm">
              No keywords are selected
            </VoxelAlert>
          </div>
        )}
      </div>

      <div className="pt-4 pb-3 px-4">
        <VoxelInput
          labelPosition="top"
          //size='sm'
          placeholder="Search or add tag"
          autoFocus
          onKeyDown={(e) => {
            if (searchTerm.length && e.key == 'Enter') {
              handleFreeAdd();
            }
          }}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          endIcon={
            <VoxelButton
              color="success"
              title="Add"
              size="xs"
              startIcon={<FontAwesomeIcon icon={faPlus} size="xs" />}
              onClick={() => handleFreeAdd()}
              className={`-mt-[1px] -mr-0.5
              ${!searchTerm?.length && 'opacity-0'}
              `}
              disabled={formikProps.isSubmitting}
            />
          }
        />
        <div
          className={`
                    overflow-y-auto scroll
                    select-none
                    mt-2
                    flex flex-col
                  `}
          style={{ maxHeight: 200 }}
        >
          {filteredOptions.map((opt) => (
            <VoxelSelectItem
              active={false}
              selected={false}
              key={opt.value}
              onClick={() => {
                formikProps.setFieldValue('tag', opt).then(formikProps.submitForm);
              }}
            >
              <div className="flex flex-col">
                <div className="flex flex-row items-center gap-x-2">
                  {opt.icon}
                  <span>{opt.title}</span>
                </div>
              </div>
              <span className="flex-grow" />
            </VoxelSelectItem>
          ))}
        </div>

        {!!openTagPanel && (
          <>
            <div className="h-4" />
            <VoxelDivider />
            <div className="h-4" />
            <VoxelButton
              title="Open tag panel"
              fullWidth
              color="primary"
              disabled={tagPanelOpen}
              size="sm"
              onClick={() => {
                openTagPanel(tagGroup.id);
                handleClose();
              }}
            />
          </>
        )}
      </div>
      <div className="overflow-x-hidden">
        {formikProps.isSubmitting ? <LinearProgress /> : <div className="h-1" />}
      </div>
    </div>
  );
};

export default CellApplyTagPopover;
